import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SocketService } from '../../game/services/SocketService';
import { ServerMessages } from '../../game/constants/ServerMessages';
import { UserMessages } from '../../game/constants/UserMessages';
import './Lobby.component.css';

/** Returns a consistent HSL color for a given string (e.g. player name) so each user has a unique color. */
function colorForName(name: string): string {
  let hash = 0;
  const s = name || '';
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash) + s.charCodeAt(i);
    hash |= 0;
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 75%, 55%)`;
}

const Lobby: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const socketServiceRef = useRef<SocketService | null>(null);
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Delay connect until after Strict Mode unmount
  const mainPlayerIdRef = useRef<string>('');
  const handlersSetupRef = useRef<boolean>(false);
  /** True when we opened the lobby from dashboard for a running game (observeLobby=1). Do not auto-navigate to game on GAME_START_UI / START_GAME_JOINED_PLAYER. */
  const openedAsLobbyObserverRef = useRef<boolean>(false);
  const gameWindowRef = useRef<Window | null>(null);
  const windowCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const chatMessagesRef = useRef<HTMLDivElement>(null);

  const [lobbyData, setLobbyData] = useState<{
    roomId: string;
    chips: string;
    smallBlind: string;
    bigBlind: string;
    hostId: string;
    players: Array<{ id: string; name: string }>;
    isRunning: boolean;
    isFinished: boolean;
  } | null>(null);
  const [chatMessages, setChatMessages] = useState<Array<{ playerName: string; message: string }>>([]);
  const [chatInput, setChatInput] = useState('');
  const [isHost, setIsHost] = useState(false);
  const [copyButtonText, setCopyButtonText] = useState('Copy');
  const [isCopyButtonDisabled, setIsCopyButtonDisabled] = useState(false);
  const [roomId, setRoomId] = useState<string>('');
  const [mainPlayerId, setMainPlayerId] = useState<string>('');
  const [roomError, setRoomError] = useState('');
  const [showLeaveRoomConfirm, setShowLeaveRoomConfirm] = useState(false);
  const [lobbyMessageModal, setLobbyMessageModal] = useState<string | null>(null);
  /** When set, table will close after the current hand (e.g. open 12+ hours). Shown as a banner. */
  const [tableClosingAfterHandBanner, setTableClosingAfterHandBanner] = useState<string | null>(null);

  const roomIdRef = useRef<string>('');
  /** Time we last received ROOM_JOINED (full state). Used to ignore stale PLAYER_LEFT_ROOM that arrived while tab was in background. */
  const lastRoomJoinedAtRef = useRef<number>(0);

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    const el = chatMessagesRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [chatMessages]);

  // Keep ref in sync with roomId for focus handler
  useEffect(() => {
    roomIdRef.current = roomId;
  }, [roomId]);

  // When user focuses the lobby window/tab, request full state from server (players, can start, isRunning, etc.)
  useEffect(() => {
    let requestTimeoutId: ReturnType<typeof setTimeout> | null = null;
    const sendJoinRoomForState = () => {
      const currentRoomId = roomIdRef.current;
      const socket = socketServiceRef.current;
      if (!currentRoomId) {
        console.log('Lobby focus: skip, no roomId');
        return;
      }
      const token = localStorage.getItem('token');
      if (!token) return;
      if (socket?.isConnected()) {
        console.log('Lobby requesting full state for room:', currentRoomId);
        socket.sendMessage({
          type: UserMessages.JOIN_ROOM,
          roomId: currentRoomId,
          playerId: mainPlayerIdRef.current,
          playerName: localStorage.getItem('username') || 'Player',
          token,
          observeOnly: openedAsLobbyObserverRef.current === true,
        });
        return;
      }
      // Socket disconnected – try reconnect then request state (reuse same service)
      if (socket?.forceReconnect) {
        console.log('Lobby focus: socket disconnected, reconnecting then requesting state');
        socket.forceReconnect().then(() => {
          if (requestTimeoutId != null) clearTimeout(requestTimeoutId);
          requestTimeoutId = setTimeout(() => {
            requestTimeoutId = null;
            if (socketServiceRef.current?.isConnected() && roomIdRef.current) {
              socketServiceRef.current.sendMessage({
                type: UserMessages.JOIN_ROOM,
                roomId: roomIdRef.current,
                playerId: mainPlayerIdRef.current,
                playerName: localStorage.getItem('username') || 'Player',
                token: localStorage.getItem('token') || '',
                observeOnly: openedAsLobbyObserverRef.current === true,
              });
            }
          }, 400);
        }).catch((err) => console.error('Lobby reconnect on focus failed:', err));
      }
    };
    const requestLobbyState = (fromFocus: boolean) => {
      if (!fromFocus && document.visibilityState !== 'visible') return;
      if (requestTimeoutId != null) clearTimeout(requestTimeoutId);
      requestTimeoutId = setTimeout(() => {
        requestTimeoutId = null;
        sendJoinRoomForState();
      }, 150);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') requestLobbyState(false);
    };
    const onFocus = () => {
      requestLobbyState(true);
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      if (requestTimeoutId != null) clearTimeout(requestTimeoutId);
    };
  }, []);

  // Single effect: auth, roomId, socket connection and cleanup (avoids duplicate lobby sockets)
  useEffect(() => {
    const roomIdParam = searchParams.get('roomId');
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    const username = localStorage.getItem('username');

    if (!token || !userId || !username) {
      navigate('/');
      return;
    }

    setMainPlayerId(userId);
    mainPlayerIdRef.current = userId;

    if (!roomIdParam) {
      navigate('/dashboard');
      return;
    }

    setRoomId(roomIdParam);
    localStorage.setItem('currentRoomId', roomIdParam);

    // When opening lobby from dashboard for a running game, join as observer so we're not seated
    const observeLobby = searchParams.get('observeLobby') === '1' || searchParams.get('observeLobby') === 'true';
    openedAsLobbyObserverRef.current = observeLobby;
    // Delay connect so Strict Mode's first unmount clears this timeout; only the final mount connects (one attempt)
    if (socketServiceRef.current?.isConnected()) return;
    connectTimeoutRef.current = setTimeout(() => {
      connectTimeoutRef.current = null;
      connectWebSocketAndJoinRoom(token, roomIdParam, observeLobby);
    }, 150);

    return () => {
      if (connectTimeoutRef.current != null) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
      if (socketServiceRef.current) {
        console.log('Cleaning up lobby socket connection');
        socketServiceRef.current.disconnect();
        socketServiceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when roomId (searchParams) or navigate changes; connectWebSocketAndJoinRoom uses refs
  }, [searchParams, navigate]);

  const connectWebSocketAndJoinRoom = async (token: string, roomIdToJoin: string, observeLobby?: boolean) => {
    // Prevent multiple connections
    if (socketServiceRef.current && socketServiceRef.current.isConnected()) {
      console.log('Socket already connected, skipping connection');
      return;
    }

    try {
      // Clean up any existing connection first
      if (socketServiceRef.current) {
        socketServiceRef.current.disconnect();
        socketServiceRef.current = null;
      }
      
      // Reset handler setup flag when creating new connection
      handlersSetupRef.current = false;

      const socketService = new SocketService();
      socketServiceRef.current = socketService;

      await socketService.connect(token, 'lobby');

      // If ref was cleared, cleanup ran (e.g. Strict Mode unmount) – don't attach handlers or join room
      if (socketServiceRef.current !== socketService) {
        socketService.disconnect();
        return;
      }

      // Set up message handlers immediately after connection
      setupSocketHandlers(socketService);

      // Wait a moment for handlers to be set up, then join room
      setTimeout(() => {
        if (socketService.isConnected()) {
          console.log('Sending JOIN_ROOM for roomId:', roomIdToJoin, 'observeOnly:', observeLobby);
          socketService.sendMessage({
            type: UserMessages.JOIN_ROOM,
            roomId: roomIdToJoin,
            playerId: mainPlayerIdRef.current,
            playerName: localStorage.getItem('username') || 'Player',
            token: token,
            observeOnly: observeLobby === true,
          });
        } else {
          console.error('WebSocket not connected, cannot join room');
          setRoomError('Failed to connect to server. Please try again.');
        }
      }, 500);
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      setRoomError('Failed to connect to server. Please try again.');
    }
  };

  const setupSocketHandlers = (socketService: SocketService) => {
    // Always set up handlers - don't skip if already set up
    // This ensures handlers are registered even if the socket service is recreated
    console.log('Setting up socket handlers for lobby');
    
    // Clear any existing handlers first to avoid duplicates
    socketService.clearAllHandlers();
    
    handlersSetupRef.current = true;

    // Handle ROOM_JOINED
    socketService.onMessage(ServerMessages.ROOM_JOINED, (message: any) => {
      console.log('ROOM_JOINED received:', message);
      const userId = mainPlayerIdRef.current;
      const isHostUser = message.hostId === userId;

      // Convert playersNames object to array
      const playersArray: Array<{ id: string; name: string }> = [];
      if (message.playersNames) {
        Object.entries(message.playersNames).forEach(([playerId, playerName]) => {
          playersArray.push({
            id: playerId,
            name: String(playerName),
          });
        });
      }

      setLobbyData({
        roomId: message.roomId,
        chips: String(message.chips || 1000),
        smallBlind: String(message.smallBlind || 10),
        bigBlind: String(message.bigBlind || 20),
        hostId: message.hostId,
        players: playersArray,
        isRunning: message.isRunning || false,
        isFinished: false,
      });
      setIsHost(isHostUser);
      lastRoomJoinedAtRef.current = Date.now();
    });

    // Handle PLAYER_JOINED
    socketService.onMessage(ServerMessages.PLAYER_JOINED, (message: any) => {
      console.log('PLAYER_JOINED received:', message);
      setLobbyData((prevLobbyData) => {
        if (!prevLobbyData) {
          console.warn('PLAYER_JOINED received but lobbyData is null');
          return null;
        }
        
        // Check if player is already in the list (avoid duplicates)
        const playerExists = prevLobbyData.players.some(
          (p) => p.id === message.joinedPlayerId
        );
        
        if (playerExists) {
          console.log('Player already in list, skipping:', message.joinedPlayerId);
          return prevLobbyData;
        }
        
        return {
          ...prevLobbyData,
          players: [
            ...prevLobbyData.players,
            {
              id: message.joinedPlayerId,
              name: message.joinedPlayerName,
            },
          ],
        };
      });
    });

    // Handle PLAYER_LEFT_ROOM
    socketService.onMessage(ServerMessages.PLAYER_LEFT_ROOM, (message: any) => {
      const now = Date.now();
      if (now - lastRoomJoinedAtRef.current < 2000) {
        console.log('Ignoring stale PLAYER_LEFT_ROOM (received within 2s of ROOM_JOINED, likely from when tab was in background)');
        return;
      }
      console.log('PLAYER_LEFT_ROOM received:', message);
      setLobbyData((prevLobbyData) => {
        if (!prevLobbyData) {
          console.warn('PLAYER_LEFT_ROOM received but lobbyData is null');
          return null;
        }
        
        return {
          ...prevLobbyData,
          players: prevLobbyData.players.filter((p) => p.id !== message.playerId),
        };
      });
    });

    // Handle CHAT_MESSAGE
    socketService.onMessage(ServerMessages.CHAT_MESSAGE, (message: any) => {
      setChatMessages((prev) => [
        ...prev,
        {
          playerName: message.playerName || 'Unknown',
          message: message.message || '',
        },
      ]);
    });

    // Handle HOST_PLAYER_CHANGED
    socketService.onMessage(ServerMessages.HOST_PLAYER_CHANGED, (message: any) => {
      const userId = mainPlayerIdRef.current;
      const newHostId = message.newHostId || '';
      setIsHost(newHostId === userId);
      setLobbyData((prevLobbyData) => {
        if (!prevLobbyData) {
          console.warn('HOST_PLAYER_CHANGED received but lobbyData is null');
          return null;
        }
        return {
          ...prevLobbyData,
          hostId: newHostId,
        };
      });
    });

    // Handle GAME_START_UI - convert this lobby window into the game window (navigate in place). Skip if we opened as lobby observer (dashboard "Open lobby" for running game).
    socketService.onMessage(ServerMessages.GAME_START_UI, (message: any) => {
      if (openedAsLobbyObserverRef.current) {
        console.log('GAME_START_UI received but we opened as lobby observer, staying on lobby');
        setLobbyData((prevLobbyData) => {
          if (!prevLobbyData) return null;
          return { ...prevLobbyData, isRunning: true };
        });
        return;
      }
      console.log('GAME_START_UI received, converting lobby window to game');
      setLobbyData((prevLobbyData) => {
        if (!prevLobbyData) return null;
        return { ...prevLobbyData, isRunning: true };
      });
      if (message.roomId) {
        localStorage.setItem('currentRoomId', message.roomId);
        navigate(`/game?roomId=${message.roomId}`);
      }
    });

    // Handle START_GAME_JOINED_PLAYER - convert this lobby window into the game window if not already navigated. Skip if we opened as lobby observer.
    socketService.onMessage(ServerMessages.START_GAME_JOINED_PLAYER, (message: any) => {
      if (openedAsLobbyObserverRef.current) {
        console.log('START_GAME_JOINED_PLAYER received but we opened as lobby observer, staying on lobby');
        setLobbyData((prevLobbyData) => {
          if (!prevLobbyData) return null;
          return { ...prevLobbyData, isRunning: true };
        });
        return;
      }
      console.log('START_GAME_JOINED_PLAYER received, converting lobby window to game');
      setLobbyData((prevLobbyData) => {
        if (!prevLobbyData) return null;
        return { ...prevLobbyData, isRunning: true };
      });
      if (message.roomId) {
        localStorage.setItem('currentRoomId', message.roomId);
        navigate(`/game?roomId=${message.roomId}`);
      }
    });

    // Handle ROOM_CREATED - when room is created, populate lobbyData immediately
    socketService.onMessage(ServerMessages.ROOM_CREATED, (message: any) => {
      console.log('ROOM_CREATED received:', message);
      if (message.roomId) {
        const userId = mainPlayerIdRef.current;
        const isHostUser = message.hostId === userId;
        
        setRoomId(message.roomId);
        localStorage.setItem('currentRoomId', message.roomId);
        // Update URL to include roomId
        window.history.replaceState({}, '', `/lobby?roomId=${message.roomId}`);
        
        // Populate lobbyData from ROOM_CREATED message
        // The creator is automatically added as the first player
        const username = localStorage.getItem('username') || 'Player';
        setLobbyData({
          roomId: message.roomId,
          chips: String(message.chips || 1000),
          smallBlind: String(message.smallBlind || 10),
          bigBlind: String(message.bigBlind || 20),
          hostId: message.hostId,
          players: [{
            id: userId,
            name: username,
          }],
          isRunning: false, // Newly created rooms are not running
          isFinished: false,
        });
        setIsHost(isHostUser);
      }
    });

    // Handle GAME_ENDED - when all players exit the game
    socketService.onMessage(ServerMessages.GAME_ENDED, (message: any) => {
      console.log('GAME_ENDED received in lobby:', message);
      if (message.reason === 'all_players_exited') {
        setLobbyData((prevLobbyData) => {
          if (!prevLobbyData) return null;
          return {
            ...prevLobbyData,
            isRunning: false,
            isFinished: true,
          };
        });
        setTimeout(() => {
          window.close();
        }, 30000);
      }
    });

    // Handle ROOM_DELETED - room was closed (e.g. no players seated); close this window or go to dashboard
    socketService.onMessage(ServerMessages.ROOM_DELETED, (message: any) => {
      console.log('ROOM_DELETED received in lobby:', message);
      if (!message?.roomId) return;
      setTableClosingAfterHandBanner(null);
      setLobbyMessageModal('This game has been closed (no players seated).');
      const closeOrNavigate = () => {
        if (typeof window !== 'undefined' && window.opener && !window.opener.closed) {
          window.close();
        } else {
          navigate('/dashboard', { replace: true });
        }
      };
      setTimeout(closeOrNavigate, 1500);
    });

    // Table will close after current hand (e.g. open 12+ hours)
    socketService.onMessage(ServerMessages.TABLE_CLOSING_AFTER_HAND, (message: any) => {
      const reason = message?.reason || 'This table has been open for over 12 hours and will close after this hand. Finish the hand, then the table and lobby will close.';
      setTableClosingAfterHandBanner(reason);
    });

    // Handle ERROR_MESSAGE
    socketService.onMessage(ServerMessages.ERROR_MESSAGE, (message: any) => {
      console.error('ERROR_MESSAGE received in lobby:', message);
      const errorMsg = message.message || 'An error occurred';
      console.log('Processing error message:', errorMsg);
      
      // Check if error is authentication-related
      const lowerErrorMsg = errorMsg.toLowerCase();
      if (lowerErrorMsg.includes('token') || 
          lowerErrorMsg.includes('unauthorized') || 
          lowerErrorMsg.includes('authentication') ||
          (lowerErrorMsg.includes('invalid') && lowerErrorMsg.includes('session'))) {
        // Clear invalid token and redirect to login
        console.log('Authentication error detected, redirecting to login');
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        localStorage.removeItem('username');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('emailVerified');
        window.location.href = '/login';
      } else {
        console.log('Setting room error:', errorMsg);
        setRoomError(errorMsg);
        // If we get an error and don't have lobbyData, show the error (displayed in UI)
        // This helps when joining fails
      }
    });
  };


  const handleSendChat = () => {
    if (!chatInput.trim() || !socketServiceRef.current || !roomId) return;

    const username = localStorage.getItem('username') || 'Player';
    socketServiceRef.current.sendMessage({
      type: UserMessages.CHAT_MESSAGE,
      message: chatInput.trim(),
      roomId: roomId,
      playerId: mainPlayerId,
      playerName: username,
    });

    setChatInput('');
  };

  const handleStartGame = () => {
    if (!socketServiceRef.current || !roomId || !isHost) return;

    socketServiceRef.current.sendMessage({
      type: UserMessages.START_GAME,
      roomId: roomId,
      playerId: mainPlayerId,
    });
  };

  const copyRoomId = async (roomIdToCopy: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(roomIdToCopy);
        setCopyButtonText('Copied');
        setIsCopyButtonDisabled(true);
        setTimeout(() => {
          setCopyButtonText('Copy');
          setIsCopyButtonDisabled(false);
        }, 3000);
        return;
      }

      const textArea = document.createElement('textarea');
      textArea.value = roomIdToCopy;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          setCopyButtonText('Copied');
          setIsCopyButtonDisabled(true);
          setTimeout(() => {
            setCopyButtonText('Copy');
            setIsCopyButtonDisabled(false);
          }, 3000);
        }
      } finally {
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy room ID:', err);
      prompt('Copy this Room ID:', roomIdToCopy);
    }
  };

  const handleLeaveRoomClick = () => {
    setShowLeaveRoomConfirm(true);
  };

  const handleLeaveRoomConfirm = () => {
    setShowLeaveRoomConfirm(false);
    if (socketServiceRef.current && roomId && mainPlayerId) {
      socketServiceRef.current.sendMessage({
        type: UserMessages.EXIT_GAME,
        roomId: roomId,
        playerId: mainPlayerId,
      });
      socketServiceRef.current.sendMessage({
        type: UserMessages.LEAVE_ROOM,
        roomId: roomId,
        playerId: mainPlayerId,
      });
    }
    if (socketServiceRef.current) {
      socketServiceRef.current.disconnect();
    }
    localStorage.removeItem('currentRoomId');
    if (gameWindowRef.current && !gameWindowRef.current.closed) {
      gameWindowRef.current.close();
      gameWindowRef.current = null;
    }
    window.close();
  };

  // Open game in a new popup (only host start converts lobby to game in place)
  const handleOpenGameWindow = () => {
    if (!lobbyData || !lobbyData.roomId) return;
    if (gameWindowRef.current && !gameWindowRef.current.closed) {
      gameWindowRef.current.focus();
      return;
    }
    const isSeatedInGame = lobbyData.isRunning && lobbyData.players.some((p) => p.id === mainPlayerId);
    const observeOnly = lobbyData.isRunning && !isSeatedInGame;
    const gameUrl = `${window.location.origin}/game?roomId=${lobbyData.roomId}${observeOnly ? '&observeOnly=1' : ''}`;
    const gameWindow = window.open(
      gameUrl,
      `game-${lobbyData.roomId}`,
      'width=1200,height=800,resizable=yes,scrollbars=yes'
    );
    if (!gameWindow) {
      setLobbyMessageModal('Please allow popups to open the game table');
    } else {
      gameWindowRef.current = gameWindow;
      gameWindow.focus();
      if (windowCheckIntervalRef.current) clearInterval(windowCheckIntervalRef.current);
      windowCheckIntervalRef.current = setInterval(() => {
        if (gameWindowRef.current && gameWindowRef.current.closed) {
          gameWindowRef.current = null;
          if (windowCheckIntervalRef.current) {
            clearInterval(windowCheckIntervalRef.current);
            windowCheckIntervalRef.current = null;
          }
        }
      }, 1000);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (socketServiceRef.current) {
        socketServiceRef.current.disconnect();
      }
      // Clear game window ref and interval on unmount
      if (windowCheckIntervalRef.current) {
        clearInterval(windowCheckIntervalRef.current);
        windowCheckIntervalRef.current = null;
      }
      gameWindowRef.current = null;
    };
  }, []);


  if (!lobbyData) {
    return (
      <div className="lobby-loading">
        {lobbyMessageModal && (
          <div className="lobby-message-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="lobby-message-modal-title">
            <div className="lobby-message-modal">
              <div className="lobby-message-modal-header">
                <h2 id="lobby-message-modal-title" className="lobby-message-modal-title">Message</h2>
                <button type="button" className="lobby-message-modal-close" onClick={() => setLobbyMessageModal(null)} aria-label="Close">×</button>
              </div>
              <p className="lobby-message-modal-text">{lobbyMessageModal}</p>
              <div className="lobby-message-modal-actions">
                <button type="button" className="lobby-message-modal-btn" onClick={() => setLobbyMessageModal(null)}>OK</button>
              </div>
            </div>
          </div>
        )}
        <h2>Connecting to lobby...</h2>
        {roomError && (
          <div className="room-error" style={{ marginTop: '1rem', maxWidth: '500px' }}>
            {roomError}
            <div style={{ marginTop: '1rem' }}>
              <button 
                onClick={() => {
                  setRoomError('');
                  const roomIdParam = searchParams.get('roomId');
                  const token = localStorage.getItem('token');
                  if (roomIdParam && token) {
                    connectWebSocketAndJoinRoom(token, roomIdParam);
                  }
                }}
                className="btn-primary"
                style={{ marginRight: '0.5rem' }}
              >
                Retry
              </button>
              <button 
                onClick={() => navigate('/dashboard')}
                className="btn-secondary"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="game-lobby">
      {tableClosingAfterHandBanner && (
        <div className="lobby-table-closing-banner" role="alert">
          {tableClosingAfterHandBanner}
        </div>
      )}
      {lobbyMessageModal && (
        <div className="lobby-message-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="lobby-message-modal-title">
          <div className="lobby-message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lobby-message-modal-header">
              <h2 id="lobby-message-modal-title" className="lobby-message-modal-title">Message</h2>
              <button type="button" className="lobby-message-modal-close" onClick={() => setLobbyMessageModal(null)} aria-label="Close">×</button>
            </div>
            <p className="lobby-message-modal-text">{lobbyMessageModal}</p>
            <div className="lobby-message-modal-actions">
              <button type="button" className="lobby-message-modal-btn" onClick={() => setLobbyMessageModal(null)}>OK</button>
            </div>
          </div>
        </div>
      )}
      {showLeaveRoomConfirm && (
        <div className="lobby-modal-overlay" onClick={() => setShowLeaveRoomConfirm(false)}>
          <div className="lobby-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Leave room?</h3>
            <p>You will be exiting the game and will be sat out. Continue?</p>
            <div className="lobby-modal-actions">
              <button className="btn-primary" onClick={handleLeaveRoomConfirm}>Yes, leave</button>
              <button className="btn-secondary" onClick={() => setShowLeaveRoomConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      <div className="lobby-container">
        <div className="lobby-header">
          <h2>Game Lobby</h2>
          <div className="lobby-header-actions">
            {isHost && !lobbyData.isRunning && (
              <button
                className="start-game-btn"
                onClick={handleStartGame}
                disabled={lobbyData.players.length < 2}
              >
                Start Game {lobbyData.players.length < 2 && '(Need at least 2 players)'}
              </button>
            )}
            {lobbyData.isRunning && !lobbyData.isFinished && (
              <button
                className="open-game-btn"
                onClick={handleOpenGameWindow}
              >
                Open table
              </button>
            )}
            {lobbyData.isFinished && (
              <div className="game-finished-message">
                Game finished - All players exited. Window will close in 30 seconds...
              </div>
            )}
            {lobbyData.isRunning && lobbyData.players.some((p) => p.id === mainPlayerId) ? (
              <button onClick={handleLeaveRoomClick} className="leave-room-btn">Leave game</button>
            ) : (
              <button onClick={handleLeaveRoomClick} className="leave-room-btn">Leave Room</button>
            )}
          </div>
        </div>

        <div className="lobby-content">
          <div className="lobby-left">
            <div className="game-settings">
              <h3>Game Settings</h3>
              <div className="setting-item">
                <span className="setting-label">Game Status:</span>
                <span className={`setting-value ${
                  lobbyData.isFinished ? 'status-finished' : 
                  lobbyData.isRunning ? 'status-running' : 'status-waiting'
                }`}>
                  {lobbyData.isFinished ? '🔴 Finished' : 
                   lobbyData.isRunning ? '🟢 Running' : '⏸️ Waiting'}
                </span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Room ID:</span>
                <span className="setting-value">{lobbyData.roomId}</span>
                <button
                  className="copy-btn"
                  onClick={() => copyRoomId(lobbyData.roomId)}
                  disabled={isCopyButtonDisabled}
                >
                  {copyButtonText}
                </button>
              </div>
              <div className="setting-item">
                <span className="setting-label">Starting Chips:</span>
                <span className="setting-value">${lobbyData.chips}</span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Small Blind:</span>
                <span className="setting-value">${lobbyData.smallBlind}</span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Big Blind:</span>
                <span className="setting-value">${lobbyData.bigBlind}</span>
              </div>
            </div>

              <div className="players-list">
              <h3>Players ({lobbyData.players.length})</h3>
              <div className="players-container">
                {lobbyData.players.map((player) => (
                  <div key={player.id} className="player-item">
                    {player.name} {player.id === lobbyData.hostId && '(Host)'}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lobby-right">
            <div className="chat-container">
              <h3>Chat</h3>
              <div className="chat-messages" ref={chatMessagesRef}>
                {chatMessages.map((msg, idx) => (
                  <div key={idx} className="chat-message">
                    <span className="chat-player" style={{ color: colorForName(msg.playerName) }}>{msg.playerName}:</span>
                    <span className="chat-text">{msg.message}</span>
                  </div>
                ))}
              </div>
              <div className="chat-input-container">
                <input
                  type="text"
                  className="chat-input"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSendChat();
                    }
                  }}
                  placeholder="Type a message..."
                />
                <button onClick={handleSendChat} className="chat-send-btn">Send</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Lobby;

